import { observer } from 'mobx-react-lite';
import React from 'react';
import { toast } from 'react-toastify';
import { useGetBooking } from '#/hooks/useGetBooking';
import { useIsSmallView } from '#/hooks/useIsSmallView';
import { useUpdateReservation } from '#/hooks/useUpdateReservation';
import { amendmentStore } from '#/stores/amendmentStore';
import { RentalsReservationInstance } from '@jucy-nakuru/common/services/JucyRentalsApi/RentalsReservation';
import { useConfirmDialog } from '@jucy-ui/components';
import { ZoomSpinner } from '@jucy-ui/components/ZoomSpinner';
import { Money } from '@jucy/rentals-api-client';
import { ValidateUpdateResponse } from '@jucy/rentals-api-client/rentals-api-v3/models';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Currency from '../../Currency';
import BookingSummaryPanelSection from './BookingSummaryPanelSection';

export type AmendmentActionsProps = {
    reservation: RentalsReservationInstance;
    amendment?: ValidateUpdateResponse;
};
export const AmendmentActions: React.FC<AmendmentActionsProps> = observer(function AmendmentActionsComponent({ reservation, amendment }) {
    const { data: booking } = useGetBooking(reservation?.reservationReference);
    const [getConfirmation, ConfirmationClearAmendment] = useConfirmDialog();

    const {
        mutate: saveAmendment,
        isSuccess,
        isPending,
        reset,
    } = useUpdateReservation(reservation.reservationReference, {
        onSuccess: (data) => {
            amendmentStore.removeAmendment(data.reference);
            reset();
        },
        onError: (error) => {
            toast.error(
                <>
                    <AlertTitle>Unable to update your trip</AlertTitle>
                    {error.message}
                </>,
                { autoClose: false, closeOnClick: false, draggable: false }
            );
        },
    });

    const paymentsTotal = booking?.payments?.reduce((acc, payment) => acc + (payment.total?.value || 0), 0) || 0;
    const originalBalance = reservation?.computedBalanceDue.value || 0;
    const newTotal = amendment?.updates?.totalPrice.value || 0;
    const newBalance = {
        ...amendment?.updates?.totalPrice,
        value: newTotal - paymentsTotal,
    };
    const difference = {
        ...amendment?.updates?.totalPrice,
        value: newBalance.value - originalBalance,
    };
    const isSm = useIsSmallView();
    if (!amendment) {
        return null;
    }
    return (
        <BookingSummaryPanelSection
            sx={{
                bgcolor: 'background.paper',
                position: isSm ? 'fixed' : 'sticky',
                bottom: 0,
                left: isSm ? 0 : undefined,
                right: isSm ? 0 : undefined,
                padding: isSm ? 2 : undefined,
                boxShadow: isSm ? '0px -3px 4px rgba(0, 0, 0, 0.1)' : undefined,
                minHeight: '100px',
            }}
        >
            <Divider sx={{ mb: 3, mt: 3, display: isSm ? 'none' : 'block' }} />
            <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                <Grid size={8}>
                    <Stack
                        direction="row"
                        sx={{
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="caption">Original balance</Typography>
                        <Typography variant="caption">
                            <Currency>{reservation?.computedBalanceDue as Required<Money>}</Currency>
                        </Typography>
                    </Stack>

                    <Stack
                        direction="row"
                        sx={{
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="caption">New balance</Typography>
                        <Typography variant="caption">
                            <Currency>{newBalance}</Currency>
                        </Typography>
                    </Stack>

                    <Divider sx={{ my: 1 }} />

                    <Stack
                        direction="row"
                        sx={{
                            justifyContent: 'space-between',
                            mb: 1,
                        }}
                    >
                        <Typography variant="subtitle1">Difference</Typography>
                        <Typography variant="subtitle1">
                            <Currency>{difference}</Currency>
                        </Typography>
                    </Stack>
                </Grid>
                <Grid size={4}>
                    <Stack spacing={2} direction="column">
                        <Button
                            variant="outlined"
                            disabled={isPending}
                            onClick={async () => {
                                const result = await getConfirmation('This will permanently delete your changes');
                                if (result) {
                                    amendmentStore.removeAmendment(amendment);
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={isPending}
                            variant="contained"
                            onClick={() => {
                                saveAmendment(amendment);
                            }}
                            startIcon={isSuccess ? <CheckCircleOutlineIcon color="success" /> : undefined}
                        >
                            Confirm <ZoomSpinner show={isPending} />
                        </Button>
                    </Stack>
                </Grid>
            </Grid>
            <ConfirmationClearAmendment />
        </BookingSummaryPanelSection>
    );
});
